import { vec2 } from "@tlaukkan/tsm";
import { SimulationConstants } from "../../shared/constants";
import { TrainingLevel } from "../../shared/playerAccount";
import { ArrayVec2 } from "../../shared/synchronization";
import { GameClient } from "./gameClient";
import { getBugReportButtonRect } from "./bugReport/bugReporter";
import { IngameTutorialStep, TutorialGraphBuilder } from "./tutorialGraph";

const getCombineCellPosition = (gameClient: GameClient): ArrayVec2 => {
	const stateMachine = gameClient.tutorialStateMachine;
	const cell = stateMachine.combineTutorialCells[0];
	const position = new vec2([cell.position[0], cell.position[1]]);
	return stateMachine.getSpotlightPosition(position);
}

const getCombineSecondCellPosition = (gameClient: GameClient): ArrayVec2 => {
	const stateMachine = gameClient.tutorialStateMachine;
	const cell = stateMachine.combineTutorialCells[1];
	const position = new vec2([cell.position[0], cell.position[1]]);
	return stateMachine.getSpotlightPosition(position);
}

const getFirstCellPosition = (gameClient: GameClient): ArrayVec2 => {
	const firstCell = gameClient.getPlayer()?.immuneCells[0]!
	return gameClient.tutorialStateMachine.getSpotlightCellPosition(firstCell.id);
}

const getVirusInfoUIPosition = (gameClient: GameClient): ArrayVec2 => {
	//TODO: Actually caclulate this with ui queries
	const isLeft = !gameClient.isInspectionBlockingCell();
	return [isLeft ? 85 : 35, 80];
}

const getBotPosition = (gameClient: GameClient): ArrayVec2 => {
	const bot = gameClient.players.find(player => player.isBot === true)!;
	if (!bot) console.error(`MP tutorial has no bot player`);
	else {
		gameClient.cameraSystem.focusOnPlayer(bot);
	}

	const playerPos = new vec2([bot.spawnPoint[0], bot.spawnPoint[1]]);
	return gameClient.tutorialStateMachine.getSpotlightPosition(playerPos);
}

//Spotlight positions run the opposite way to screen percentages (100 is the left/top edge)
function getBugReportButtonPosition(): ArrayVec2 {
	const buttonRect = getBugReportButtonRect();
	const ui = document.getElementById('user-interface');
	if (!buttonRect || !ui) return [50, 50];
	const uiRect = ui.getBoundingClientRect();
	const x = (buttonRect.left + buttonRect.width / 2 - uiRect.left) / uiRect.width * 100;
	const y = (buttonRect.top + buttonRect.height / 2 - uiRect.top) / uiRect.height * 100;
	return [100 - x, 100 - y];
}

export function createTutorialGraph(gameClient: GameClient) {
	const isPlaytest = process.env.build_config === 'playtest';
	const builder = new TutorialGraphBuilder()
		.on("shopEnter", [TrainingLevel.NewPlayer], 1)
		.next(IngameTutorialStep.Welcome, isPlaytest ? "Welcome to the Auto Immune Playtest!" : "Welcome to Auto Immune!", 0, 0, [50, 50]);
	if (isPlaytest) {
		builder.next(IngameTutorialStep.ReportBugs, "If you find any issues, please report them with this button", 110, -42, getBugReportButtonPosition)
			.flashBugReportButton();
	}
	return builder
		.next(IngameTutorialStep.Viruses, "Viruses are invading from these spawn points.", 200, -30, [50, 50])
		.cameraFocus('enemy')
		.next(IngameTutorialStep.BloodCells, "They're after your blood cells! If you lose all your blood cells you lose the game.", 200, -30, [50, 50])
		.cameraFocus('player')
		.next(IngameTutorialStep.Market, "You must defend against the viruses by purchasing immune cells!", 200, 0, [50, 10])
		.next(IngameTutorialStep.Purchase, "To purchase an immune cell, click and drag the immune cell from the shop...", 200, 0, [50, 10])
		.enableButtons("immuneCell")
		.enableCellClicks()
		.waitFor('cellPreview')

		.next(IngameTutorialStep.Purchase2, "...And place it here", 200, -30, [35, 50])
		.enableCellClicks()
		.waitFor('cellPurchase')

		.next(IngameTutorialStep.Rotate, "Rotate the immune cell by hovering over it and moving the arrow.", 100, null, getFirstCellPosition)
		.enableCellClicks()
		.hideSellButton()
		.waitFor('cellRotation')

		.next(IngameTutorialStep.Gold, "Immune cells cost 2 carbs (money) each. You have 2 left.", 200, 0, [81, 20])
		.next(IngameTutorialStep.UnspentGold, "Unspent carbs are lost at end of turn.", 200, 0, [81, 20])
		.next(IngameTutorialStep.Selling, "You can always sell an immune cell for 1 carb by dragging it into the sell area on the left or right.", 250, 0, [88, 50])
		.forceShowSellButton()
		.next(IngameTutorialStep.NextRound, "When you are ready, press the next round button to end the turn.", 100, 0, [13, 10])

		.on("shopEnter", [TrainingLevel.NewPlayer], 2)
		.next(IngameTutorialStep.StateReset, "Every turn your immune cells reset themselves.", 400, -30, [50, 50])
		//.next(IngameTutorialStep.Population, "The amount of new immune cells you can buy is limited by your population.", 100, 0, [50, 5])
		.next(IngameTutorialStep.MoneyReset, "Your carbs (money) also reset. You get +1 every turn.", 200, 0, [81, 20])
		.next(IngameTutorialStep.Experience, "And +1 EXP at the start of each turn.", 100, 0, [85, 10])
		.next(IngameTutorialStep.BuyingExp, "You can also buy more EXP for 3 carbs.", 100, 0, [85, 10])
		.enableButtons("experience") //spending the carbs on cells first would leave too few to buy the EXP
		.waitFor('buyExperience')


		.on("levelUp", [TrainingLevel.NewPlayer], "oncePerSession")
		.next(IngameTutorialStep.LevelUp, "When you gain enough EXP, you level up", 100, 0, [85, 10])
		.next(IngameTutorialStep.LevelUp2, "Leveling up increases your max immune cells and gives you access to powerful new immune cells.", 100, 0, [50, 5])

		.on("shopEnter", [TrainingLevel.NewPlayer], 3)
		.next(IngameTutorialStep.CameraOverVirus, "Use Right click and drag (or use WASD) to move your camera", 400, -30, [50, 50])
		.waitFor('cameraPan')
		//.enableCellClicks() //this was in the original but shouldn't be needed I think
		.next(IngameTutorialStep.CameraOverVirus2, "Scrollwheel zooms in and out", 400, -30, [50, 50])
		.waitFor('cameraZoom')
		.next(IngameTutorialStep.VirusGrowth, "More and different types of viruses attack every turn.", 400, -30, [50, 50])
		.next(IngameTutorialStep.CameraOverVirus3, "You can mouse over the virus spawn point to learn about it.", 150, -30, [50, 50])
		.enableCellClicks()
		.cameraFocus('infected')
		.forceShowCellDescription()
		.waitFor('inspectInfected')
		.next(IngameTutorialStep.VirusInformation, "Different viruses have different abilities and strengths.", 300, 10, getVirusInfoUIPosition)
		.forceShowCellDescription()
		.cameraFocus('none')
		.next(IngameTutorialStep.Objective, `Survive ${SimulationConstants.MaxRoundsByTrainingLevel[TrainingLevel.NewPlayer]} rounds to win the training game!`, 300, -40, [5, 100])
		.cameraFocus('player')
		.next(IngameTutorialStep.HaveFun, "Good Luck!", 0, 0, [0, 0])

		//Any time 
		.on("boughtCombinable", [TrainingLevel.NewPlayer, TrainingLevel.SecondGame, TrainingLevel.MultiplayerIntro], "oncePerSession")
		.next(IngameTutorialStep.CombinePrompt, "Immune cells with the same name can be combined.", 100, null, getCombineCellPosition)
		.next(IngameTutorialStep.CombineInstruction, "Combine these two cells by dragging and dropping one onto the other!", 100, null, getCombineSecondCellPosition)

		.on("combineCell", [TrainingLevel.NewPlayer, TrainingLevel.SecondGame, TrainingLevel.MultiplayerIntro], "oncePerSession")
		.next(IngameTutorialStep.Combining, "Combining immune cells adds together their health, and uses your space efficiently.", 100, null, getCombineCellPosition)
		.next(IngameTutorialStep.Elites, "Combine 3 to make a more powerful elite cell, and get a unique reward!", 100, null, getCombineCellPosition)

		.on("rewardCell", [TrainingLevel.NewPlayer, TrainingLevel.SecondGame, TrainingLevel.MultiplayerIntro], "oncePerSession")
		.next(IngameTutorialStep.RewardCell, "Reward Cells are powerful cells you get for free for combining 3 immune cells.", 300, 0, [40, 10])
		.next(IngameTutorialStep.RewardCell2, "These cells can only be obtained this way, so combine often!", 300, 0, [40, 10])

		.on("populationLimitReached", [TrainingLevel.NewPlayer, TrainingLevel.SecondGame, TrainingLevel.MultiplayerIntro], "oncePerSession")
		.next(IngameTutorialStep.Capacity1, "You have reached your max immune cell capacity!", 100, 0, [50, 5])
		.next(IngameTutorialStep.Capacity2, "You can increase your capacity by leveling up.", 100, 0, [85, 10])
		.next(IngameTutorialStep.Capacity3, "Or make room by selling immune cells...", 250, 0, [88, 50])
		.forceShowSellButton()
		.next(IngameTutorialStep.Capacity4, "...Or by combining immune cells of the same type.", 0, 0, [0, 0])

		//Training 2
		.on('shopEnter', [TrainingLevel.SecondGame], 1)
		.next(IngameTutorialStep.TrainingLevel2, "You have advanced to Level 2.", 0, 0, [50, 50])
		.next(IngameTutorialStep.TrainingLevel2Turns, `Now you have to survive ${SimulationConstants.MaxRoundsByTrainingLevel[TrainingLevel.SecondGame]} turns.`, 300, 0, [5, 100])

		.on("shopEnter", [TrainingLevel.SecondGame], 3)
		.next(IngameTutorialStep.Refresh, "This button refreshes the shop with new immune cells for 1 carb.", 100, 0, [81, 10])
		.next(IngameTutorialStep.Freeze, "This button freezes the shop. Use it hold cells to buy in later rounds.", 100, 0, [76, 10])

		//Want to force combine prompt to show up!

		//Multiplayer
		.on("recapEnter", [TrainingLevel.MultiplayerIntro], 1)
		.next(IngameTutorialStep.MultiplayerIntro, "In multiplayer you will match your immune defenses against other players.", 800, 0, [50, 50])
		.next(IngameTutorialStep.MultiplayerIntro2, "You can play realtime with your friends, but to earn rank on the ladder, you will compete against REPLAYS of other player's builds.", 800, 0, [50, 50])
		.next(IngameTutorialStep.MultiplayerIntro3, "You might recognize this player you are against now.", 500, -30, getBotPosition)
		//.cameraFocus('bot') //problem is we are zoomed out right now...
		.next(IngameTutorialStep.MultiplayerIntro4, "They were you, last game!", 500, -30, getBotPosition)
		//.cameraFocus('bot')
		.next(IngameTutorialStep.MultiplayerIntro5, "To win, outlast them.", 800, 0, [50, 50])

		.on("recapEnter", [TrainingLevel.MultiplayerIntro], 2)
		.next(IngameTutorialStep.SharedSpace, "Players can influence each other with immune cells, bullets, tag proteins, electricity and other effects.", 800, -30, [50, 50])
		.next(IngameTutorialStep.SharedLane, "The lane you share with your opponent will emit viruses towards each of you.", 800, -30, [50, 50])

		.on("shopEnter", [TrainingLevel.MultiplayerIntro], 3)
		.next(IngameTutorialStep.MultiplayerWinCondition, "To win a multiplayer game, EITHER one player must outlast the other, or both players must survive 12 rounds.", 800, 0, [50, 50])

		.on("virusGrowthNotice", [TrainingLevel.MultiplayerIntro], 4)
		.next(IngameTutorialStep.BattleRounds1, "Starting turn 3 TIEBREAKERS are active.", 200, 0, [50, 60])
		.next(IngameTutorialStep.BattleRounds2, "If neither player loses bloodcells, the shared lane will KEEP SPAWNING VIRUSES until a bloodcell dies.", 800, -30, [50, 50])
		.next(IngameTutorialStep.BattleRound3, "This tiebreaker virus growth is permanent.", 800, -30, [50, 50])
		.cameraFocus('middle')

		.on("tiebreaker", [TrainingLevel.MultiplayerIntro], "oncePerSession")
		.next(IngameTutorialStep.Tiebreaker, "Neither player took any damage, so a tiebreaker will now commence.", 500, 0, [50, 80])
		.next(IngameTutorialStep.Tiebreaker2, "The shared lane will immediatly spawn new viruses, and keep doing so up to three times or until one player loses a bloodcell.", 400, -30, [50, 50])
		.next(IngameTutorialStep.Tiebreaker3, "Virus growth from tiebreakers is permanent. So, if both players are strong, the virus gets stronger as well.", 0, 0, [50, 50])
		.graph
}